import {tva} from "@gluestack-ui/nativewind-utils/tva";
import type React from "react";
import {useCallback, useMemo, useRef, useState} from "react";
import type {LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent, View} from "react-native";
import {ScrollView} from "react-native";

import {memoWithType} from "../../react";
import {HStack} from "../ui/hstack";
import type {PressableRef} from "../ui/pressable";

import {TabGroupContext, type TabGroupContextType} from "./context";

export interface Props<T = string> {
    size?: "sm" | "md";
    activeTab: T;
    children: React.ReactElement<{active: boolean}>[];
    testID?: string;
    className?: string;
    onTabChange?: (id: T) => void;
}

const styles = tva({
    base: "border border-solid border-blueGray-200 rounded-sm bg-blueGray-50 flex-1 overflow-x-auto",
    variants: {
        size: {
            sm: "p-0.5",
            md: "p-1"
        }
    }
});

const scrollViewStyles = tva({
    base: "grow-0"
});

const isNativeElement = (tabElement: PressableRef | undefined): tabElement is View => {
    return !!tabElement && "measureLayout" in tabElement;
};

function TabGroup<T = string>({className, activeTab, size = "md", onTabChange, ...rest}: Props<T>) {
    const [tab, setTab] = useState(activeTab);
    const scrollXRef = useRef(0);
    const scrollViewWidthRef = useRef(300);
    const scrollViewRef = useRef<ScrollView>(null);
    const tabWrapperRef = useRef<View>(null);
    const tabRefs = useRef<Map<T, PressableRef>>(new Map());

    const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
        scrollXRef.current = event.nativeEvent.contentOffset.x;
    }, []);

    const handleLayout = useCallback((event: LayoutChangeEvent) => {
        scrollViewWidthRef.current = event.nativeEvent.layout.width;
    }, []);

    const scrollToTab = useCallback((tabId: T) => {
        const tabElement = tabRefs.current.get(tabId);
        if (process.env.IS_WEB && tabElement instanceof HTMLButtonElement) {
            tabElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest"
            });
        }
        if (
            !process.env.IS_WEB &&
            tabElement &&
            scrollViewRef.current &&
            "measureLayout" in tabElement &&
            tabWrapperRef.current &&
            isNativeElement(tabElement)
        ) {
            tabElement.measureLayout(
                tabWrapperRef.current,
                (x: number, _: number, width: number) => {
                    // Get current scroll position from state
                    const currentScrollX = scrollXRef.current;
                    // Get scroll view width from ref
                    const scrollViewWidth = scrollViewWidthRef.current;

                    // Calculate if element is partially visible
                    const elementLeft = x;
                    const elementRight = x + width;
                    const visibleLeft = currentScrollX;
                    const visibleRight = currentScrollX + scrollViewWidth;

                    // Check if element is partially hidden
                    const isPartiallyHidden =
                        elementLeft < visibleLeft || elementRight > visibleRight;

                    if (isPartiallyHidden) {
                        let targetX = currentScrollX;

                        // If element is cut off on the left
                        if (elementLeft < visibleLeft) {
                            targetX = elementLeft - 10; // Add small padding
                        }
                        // If element is cut off on the right
                        else if (elementRight > visibleRight) {
                            targetX = elementRight - scrollViewWidth + 10; // Add small padding
                        }

                        // Ensure we don't scroll beyond bounds
                        targetX = Math.max(0, targetX);

                        scrollViewRef.current?.scrollTo({
                            x: targetX,
                            y: 0,
                            animated: true
                        });
                    }
                }
            );
        }
    }, []);

    const handleTabChange = useCallback(
        (id: T) => {
            setTab(id);
            scrollToTab(id);
            onTabChange?.(id);
        },
        [onTabChange, scrollToTab]
    );

    const registerTabRef = useCallback((id: T, ref: PressableRef) => {
        tabRefs.current.set(id, ref);
    }, []);

    const contextValue = useMemo<TabGroupContextType<T>>(
        () => ({size, tab, onTabChange: handleTabChange, registerTabRef}),
        [size, tab, handleTabChange, registerTabRef]
    );

    return (
        <TabGroupContext.Provider value={contextValue}>
            <ScrollView
                ref={scrollViewRef}
                horizontal
                className={scrollViewStyles({className})}
                contentContainerClassName="grow"
                scrollEventThrottle={16}
                showsHorizontalScrollIndicator={!!process.env.IS_WEB}
                onLayout={handleLayout}
                onScroll={handleScroll}
            >
                <HStack
                    ref={tabWrapperRef}
                    aria-label="tab-group"
                    className={styles({size})}
                    space="xs"
                    {...rest}
                />
            </ScrollView>
        </TabGroupContext.Provider>
    );
}

export default memoWithType(TabGroup);
